import { ARCHETYPE_META } from "@/data/quiz";
import { useArchetypeCopy, getComboFrom, getShadowFrom } from "@/data/results";

/**
 * Tarjetón de Identidad Estratégica.
 *
 * Pieza de IDENTIDAD, no de venta. Responde:
 *   "¿Cómo juego naturalmente bajo presión?"
 *
 * Estética: credencial estratégica científica/tecnológica.
 * No tarot. No carta de juego. No test de personalidad.
 * Sin botones, sin CTAs, sin promo.
 */
export default function IdentidadCard({ lead }) {
  const { copy: archetypeCopy } = useArchetypeCopy();
  if (!lead) return null;
  const dominantCode = lead.archetype;
  const secondaryCode = lead.secondary;
  const dom = ARCHETYPE_META[dominantCode];
  const sec = secondaryCode ? ARCHETYPE_META[secondaryCode] : null;
  if (!dom) return null;

  const combo = getComboFrom(archetypeCopy, dominantCode, secondaryCode);
  const shadow = getShadowFrom(archetypeCopy, dominantCode);

  // Percentages (normalized) for all 4 axes — total = 100.
  const scores = lead.scores || {};
  const total = Object.values(scores).reduce((a, b) => a + (b || 0), 0) || 1;
  const pct = (code) => Math.round(((scores[code] || 0) / total) * 100);
  const axes = [
    { code: "leon", label: "Acción", value: pct("leon") },
    { code: "lobo", label: "Análisis", value: pct("lobo") },
    { code: "halcon", label: "Visión", value: pct("halcon") },
    { code: "delfin", label: "Conexión", value: pct("delfin") },
  ];

  // Short serial / credential code (deterministic from lead.id)
  const credential = (lead.id || "").slice(0, 8).toUpperCase();

  return (
    <section
      data-testid="identidad-card"
      className="relative overflow-hidden border border-white/10 rounded-md bg-gradient-to-b from-[#0A0A0A] to-[#050505]"
    >
      {/* Tech grid background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(41,188,155,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(41,188,155,0.4) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Corner brackets — credential feel */}
      <CornerBrackets />

      {/* Header — credential meta */}
      <div className="relative px-8 sm:px-10 pt-8 flex items-center justify-between text-[10px] tracking-[0.32em] uppercase font-semibold text-white/40">
        <div className="flex items-center gap-3">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#29BC9B] animate-pulse" />
          <span>Identidad estratégica · PYG/24</span>
        </div>
        <div data-testid="identidad-credential">ID · {credential}</div>
      </div>

      {/* Main grid */}
      <div className="relative px-8 sm:px-10 pt-10 pb-12 grid lg:grid-cols-[1.1fr_1fr] gap-10">
        {/* LEFT — avatars + identity */}
        <div className="space-y-8">
          <div className="flex items-end gap-6">
            {/* Dominant */}
            <div className="relative">
              <div
                className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border border-white/10 bg-black/50 flex items-center justify-center overflow-hidden"
                style={{ boxShadow: `0 0 60px ${dom.color}22` }}
              >
                <img
                  src={dom.img}
                  alt={dom.name}
                  className="w-[78%] h-[78%] object-contain"
                  data-testid="identidad-dominant-img"
                />
              </div>
              <div
                className="absolute -bottom-2 -right-2 text-[9px] tracking-[0.24em] uppercase font-bold px-2 py-1 bg-[#29BC9B] text-[#050505] rounded-sm"
              >
                Dominante
              </div>
            </div>
            {/* Secondary */}
            {sec && (
              <div className="relative pb-4">
                <div
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-white/10 bg-black/50 flex items-center justify-center overflow-hidden opacity-90"
                  style={{ boxShadow: `0 0 30px ${sec.color}1A` }}
                >
                  <img
                    src={sec.img}
                    alt={sec.name}
                    className="w-[75%] h-[75%] object-contain"
                    data-testid="identidad-secondary-img"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 text-[9px] tracking-[0.24em] uppercase font-medium px-2 py-1 bg-white/10 text-white/70 rounded-sm">
                  Secundario
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 pt-2">
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 font-semibold">
              Combinación estratégica
            </div>
            <h2
              data-testid="identidad-combo-title"
              className="text-3xl sm:text-4xl font-semibold leading-[1.05] tracking-tight"
            >
              {combo ? combo.title : dom.name}
            </h2>
            <p
              data-testid="identidad-emblema"
              className="text-[15px] text-white/55 italic leading-relaxed max-w-[460px]"
            >
              {'"Así juego naturalmente bajo presión."'}
            </p>
          </div>

          {/* Stage indicator */}
          <div className="flex items-center gap-3 pt-4 border-t border-white/5">
            <div className="text-[9px] tracking-[0.32em] uppercase text-white/40 font-semibold">
              Etapa actual
            </div>
            <div
              data-testid="identidad-stage"
              className="text-[11px] tracking-[0.22em] uppercase text-[#29BC9B] font-bold"
            >
              01 · Identificación
            </div>
          </div>
        </div>

        {/* RIGHT — strengths/risk/axes */}
        <div className="space-y-7">
          <DataBlock
            label="Fortaleza natural"
            value={dom.description.split(".")[0] + "."}
            testId="identidad-fortaleza"
          />
          <DataBlock
            label="Riesgo principal · Modo sombra"
            value={shadow?.danger || ""}
            tone="warn"
            testId="identidad-riesgo"
          />

          <div className="space-y-3 pt-2">
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 font-semibold">
              Distribución de ejes
            </div>
            <div className="space-y-2.5" data-testid="identidad-axes">
              {axes.map((a) => {
                const meta = ARCHETYPE_META[a.code];
                const isDom = a.code === dominantCode;
                return (
                  <div key={a.code} className="space-y-1">
                    <div className="flex items-center justify-between text-[12px]">
                      <span
                        className={`tracking-[0.12em] uppercase font-medium ${
                          isDom ? "text-white" : "text-white/55"
                        }`}
                      >
                        {meta.name} · {a.label}
                      </span>
                      <span
                        className={`tabular-nums font-semibold ${
                          isDom ? "text-[#29BC9B]" : "text-white/45"
                        }`}
                      >
                        {a.value}%
                      </span>
                    </div>
                    <div className="h-[3px] w-full bg-white/5 overflow-hidden rounded-full">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{
                          width: `${a.value}%`,
                          background: isDom ? "#29BC9B" : "rgba(255,255,255,0.25)",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer line — credential feel */}
      <div className="relative px-8 sm:px-10 py-4 border-t border-white/5 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-white/30">
        <span>Play Your Game™ · Evolución Estratégica</span>
        <span>Sistema · Inteligencia bajo presión</span>
      </div>
    </section>
  );
}

function DataBlock({ label, value, tone, testId }) {
  const accent =
    tone === "warn" ? "text-[#FFB76B]" : "text-[#29BC9B]";
  return (
    <div className="space-y-1.5">
      <div className={`text-[10px] tracking-[0.32em] uppercase font-semibold ${accent}`}>
        {label}
      </div>
      <p
        data-testid={testId}
        className="text-[15px] text-white/80 leading-relaxed"
      >
        {value}
      </p>
    </div>
  );
}

function CornerBrackets() {
  const cls =
    "absolute w-5 h-5 border-[#29BC9B]/40 pointer-events-none";
  return (
    <>
      <div className={`${cls} top-3 left-3 border-l border-t`} />
      <div className={`${cls} top-3 right-3 border-r border-t`} />
      <div className={`${cls} bottom-3 left-3 border-l border-b`} />
      <div className={`${cls} bottom-3 right-3 border-r border-b`} />
    </>
  );
}
